import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import { ArrowBack, ExpandMore, ChevronRight } from '@material-ui/icons';
import NumberFormat from 'react-number-format';
import clsx from 'clsx';
import { IDeath, IGame } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { getAbility } from 'utilities/ability_icons';
import { classImages } from 'utilities/ClassImages';
import { ClassColors, TeamColors } from 'utilities/team_colors';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins.
// Modelled on the damage monitor's breakdown (components/UI Overlay/DamageMonitor/DamageHealing.tsx).

const ABILITY_BAR = '#1e6fa8';

/** One player's hits on another with one ability, over the whole game. */
interface IAbilityHit {
  attacker: string;
  victim: string;
  ability: string;
  value: number;
  count: number;
}

enum Mode {
  Done = 'done',
  Taken = 'taken',
}

enum Detail {
  Overall = 'overall',
  ByPlayer = 'byPlayer',
}

/**
 * Every hit in the game: each death records the damage its victim took since their previous death, and the game's
 * extraDamage (written when it ends) what everyone took after their last death.
 */
function getHits(game: IGame): IAbilityHit[] {
  const deaths: IDeath[] = [...(game.deaths || []), ...((game as any).info?.extraDamage || [])];
  const players = new Set((game.players || []).map((p) => p.name));
  const hits = new Map<string, IAbilityHit>();
  for(const death of deaths){
    const victim = death.victim?.name || '';
    for(const d of death.damage || []){
      const attacker = d.name || '';
      // only players in this game (not NPCs or bystanders) and never their own damage
      if(!attacker || !victim || attacker == victim || !players.has(attacker) || !players.has(victim))
        continue;
      const ability = d.ability || 'Unknown';
      const key = `${attacker}|${victim}|${ability}`;
      const hit = hits.get(key);
      if(hit){
        hit.value += d.value || 0;
        hit.count += d.count || 0;
      }else{
        hits.set(key, {attacker, victim, ability, value: d.value || 0, count: d.count || 0});
      }
    }
  }
  return Array.from(hits.values());
}

interface IGroup {
  key: string;
  value: number;
  count: number;
  children: IGroup[];
}

/** Groups hits by `by`, each group broken down by `then`, both sorted by value. */
function group(hits: IAbilityHit[], by: keyof IAbilityHit, then?: keyof IAbilityHit): IGroup[] {
  const groups = new Map<string, IAbilityHit[]>();
  hits.forEach((hit) => {
    const key = String(hit[by]);
    groups.set(key, [...(groups.get(key) || []), hit]);
  });
  return Array.from(groups.entries()).map(([key, list]) => ({
    key,
    value: list.reduce((sum, hit) => sum + hit.value, 0),
    count: list.reduce((sum, hit) => sum + hit.count, 0),
    children: then ? group(list, then) : [],
  })).sort((a, b) => b.value - a.value);
}

const useStyles = makeStyles((theme) => ({
  root: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: 12,
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  title: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: theme.spacing(1),
    alignItems: 'center',
    minWidth: 0,
  },
  kicker: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    opacity: 0.6,
  },
  name: {
    fontSize: '1.05rem',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  back: {
    width: 30,
    height: 30,
    borderRadius: 7,
    color: 'rgba(255, 255, 255, 0.75)',
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.08)',
    },
  },
  tabs: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 3,
    padding: 3,
    borderRadius: 9,
    background: 'rgba(0, 0, 0, 0.35)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  tab: {
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.65)',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': {
      color: '#fff',
    },
  },
  tabActive: {
    color: '#fff',
    background: 'rgba(66, 165, 245, 0.18)',
    boxShadow: 'inset 0 0 0 1px #42a5f5',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '36px minmax(0, 1fr) 70px 110px',
    gridGap: theme.spacing(1),
    padding: theme.spacing(0, 1, 0.75),
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.5,
    '& > :nth-child(n+3)': {
      textAlign: 'right',
    },
  },
  row: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '36px minmax(0, 1fr) 70px 110px',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(0.5, 1, 0.5, 0.5),
    marginBottom: 3,
    borderRadius: 6,
    overflow: 'hidden',
    textAlign: 'left',
    justifyContent: 'stretch',
    background: 'rgba(255, 255, 255, 0.035)',
    transition: 'background 0.12s',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '& > *': {
      position: 'relative',
    },
  },
  bar: {
    position: 'absolute !important' as any,
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 6,
    transition: 'width 0.3s ease',
  },
  icon: {
    width: 32,
    height: 32,
    display: 'block',
    borderRadius: 5,
  },
  label: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    alignItems: 'center',
    minWidth: 0,
    fontWeight: 700,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)',
  },
  labelText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sub: {
    marginLeft: 6,
    fontSize: '0.8em',
    fontWeight: 600,
    opacity: 0.7,
  },
  chevron: {
    fontSize: 16,
    marginRight: 2,
    opacity: 0.6,
  },
  number: {
    textAlign: 'right',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)',
  },
  share: {
    textAlign: 'right',
    fontSize: '0.85em',
    opacity: 0.75,
    fontVariantNumeric: 'tabular-nums',
  },
  nested: {
    margin: theme.spacing(0, 0, 0.75, 2.5),
    paddingLeft: theme.spacing(0.75),
    borderLeft: '2px solid rgba(255, 255, 255, 0.1)',
  },
  teamDot: {
    position: 'absolute !important' as any,
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 2,
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    fontSize: '0.9rem',
    opacity: 0.6,
  },
  footnote: {
    marginTop: theme.spacing(1.25),
    fontSize: '0.75rem',
    opacity: 0.5,
  },
}));

interface Props {
  game: IGame;
}

/** The Abilities tab: damage by ability for every player over the whole game, like the damage monitor's breakdown. */
export const AbilityBreakdown: React.FC<Props> = ({game}) => {
  const classes = useStyles();
  const [mode, setMode] = useState<Mode>(Mode.Done);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail>(Detail.Overall);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const hits = useMemo(() => getHits(game), [game.deaths, (game as any).info?.extraDamage, game.players]);
  const playerOf = (name: string) => (game.players || []).find((p) => p.name == name);
  const classOf = (name: string) => (playerOf(name)?.class || '') as AocClasses;

  // the player list: damage each player dealt (or took)
  const players = useMemo(() => group(hits, mode == Mode.Done ? 'attacker' : 'victim'), [hits, mode]);
  // one player's breakdown, by ability or by the other player
  const mine = useMemo(() => selected ? hits.filter((hit) => (mode == Mode.Done ? hit.attacker : hit.victim) == selected) : [], [hits, mode, selected]);
  const other: keyof IAbilityHit = mode == Mode.Done ? 'victim' : 'attacker';
  const rows = useMemo(() => detail == Detail.Overall ? group(mine, 'ability', other) : group(mine, other, 'ability'), [mine, detail, other]);

  const toggle = (key: string) => setExpanded((current) => ({...current, [key]: !current[key]}));
  const pick = (name: string) => {
    setSelected(name);
    setDetail(Detail.Overall);
    setExpanded({});
  };

  const playerRow = (name: string, value: number, max: number, total: number, options: {onClick?: () => void, chevron?: boolean, open?: boolean, faded?: boolean} = {}) => {
    const aocClass = classOf(name);
    const team = playerOf(name)?.team;
    const icon = aocClass ? classImages.getColorIcon(aocClass) : '';
    return (
      <ButtonBase key={name} component="div" className={classes.row} onClick={options.onClick} disabled={!options.onClick}>
        <span className={classes.bar} style={{width: `${max > 0 ? value / max * 100 : 0}%`, background: ClassColors(aocClass) || ABILITY_BAR, opacity: options.faded ? 0.35 : 0.55}} />
        {team != null && <span className={classes.teamDot} style={{background: TeamColors[team]}} />}
        {icon ? <img src={icon} title={aocClass} className={classes.icon} /> : <span />}
        <span className={classes.label}>
          {options.chevron ? (options.open ? <ExpandMore className={classes.chevron} /> : <ChevronRight className={classes.chevron} />) : <span />}
          <span className={classes.labelText}>{name}</span>
        </span>
        <span className={classes.share}>{total > 0 ? `${Math.round(value / total * 100)}%` : ''}</span>
        <span className={classes.number}><NumberFormat value={value} displayType="text" thousandSeparator={true} decimalScale={0} /></span>
      </ButtonBase>
    );
  };

  const abilityRow = (key: string, ability: string, count: number, value: number, max: number, total: number, options: {onClick?: () => void, chevron?: boolean, open?: boolean, faded?: boolean, attacker?: string} = {}) => (
    <ButtonBase key={key} component="div" className={classes.row} onClick={options.onClick} disabled={!options.onClick}>
      <span className={classes.bar} style={{width: `${max > 0 ? value / max * 100 : 0}%`, background: ABILITY_BAR, opacity: options.faded ? 0.45 : 0.75}} />
      <img src={getAbility(ability, options.attacker ? classOf(options.attacker) : undefined)} title={ability} className={classes.icon} />
      <span className={classes.label}>
        {options.chevron ? (options.open ? <ExpandMore className={classes.chevron} /> : <ChevronRight className={classes.chevron} />) : <span />}
        <span className={classes.labelText}>{ability}<span className={classes.sub}>x{count}</span></span>
      </span>
      <span className={classes.share}>{total > 0 ? `${Math.round(value / total * 100)}%` : ''}</span>
      <span className={classes.number}><NumberFormat value={value} displayType="text" thousandSeparator={true} decimalScale={0} /></span>
    </ButtonBase>
  );

  const total = (list: IGroup[]) => list.reduce((sum, g) => sum + g.value, 0);
  const selectedTotal = total(rows);

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        {selected == null ?
          <div>
            <div className={classes.kicker}>Abilities</div>
            <div className={classes.name}>{mode == Mode.Done ? 'Damage done' : 'Damage taken'} by player</div>
          </div>
        :
          <div className={classes.title}>
            <ButtonBase className={classes.back} title="All players" onClick={() => setSelected(null)}><ArrowBack fontSize="small" /></ButtonBase>
            <div style={{minWidth: 0}}>
              <div className={classes.kicker}>{mode == Mode.Done ? 'Damage done' : 'Damage taken'}</div>
              <div className={classes.name}>{selected}</div>
            </div>
          </div>
        }
        <div className={classes.tabs}>
          {selected == null ? <React.Fragment>
            <ButtonBase className={clsx(classes.tab, {[classes.tabActive]: mode == Mode.Done})} onClick={() => setMode(Mode.Done)}>Damage done</ButtonBase>
            <ButtonBase className={clsx(classes.tab, {[classes.tabActive]: mode == Mode.Taken})} onClick={() => setMode(Mode.Taken)}>Damage taken</ButtonBase>
          </React.Fragment> : <React.Fragment>
            <ButtonBase className={clsx(classes.tab, {[classes.tabActive]: detail == Detail.Overall})} onClick={() => { setDetail(Detail.Overall); setExpanded({}); }}>Overall</ButtonBase>
            <ButtonBase className={clsx(classes.tab, {[classes.tabActive]: detail == Detail.ByPlayer})} onClick={() => { setDetail(Detail.ByPlayer); setExpanded({}); }}>{mode == Mode.Done ? 'By target' : 'By attacker'}</ButtonBase>
          </React.Fragment>}
        </div>
      </div>

      {hits.length == 0 ?
        <div className={classes.empty}>No player damage recorded for this game yet.</div>
      : selected == null ?
        <React.Fragment>
          <div className={classes.header}><span /><span>Player</span><span>Share</span><span>{mode == Mode.Done ? 'Damage' : 'Taken'}</span></div>
          {players.map((p) => playerRow(p.key, p.value, players[0]?.value || 0, total(players), {onClick: () => pick(p.key), chevron: true}))}
          <div className={classes.footnote}>Click a player to see their abilities.</div>
        </React.Fragment>
      : detail == Detail.Overall ?
        <React.Fragment>
          <div className={classes.header}><span /><span>Ability</span><span>Share</span><span>Damage</span></div>
          {rows.map((ability) => {
            const open = expanded[ability.key] == true;
            return <React.Fragment key={ability.key}>
              {abilityRow(ability.key, ability.key, ability.count, ability.value, rows[0]?.value || 0, selectedTotal,
                {onClick: () => toggle(ability.key), chevron: true, open, attacker: mode == Mode.Done ? selected : undefined})}
              {open &&
                <div className={classes.nested}>
                  {ability.children.map((p) => playerRow(p.key, p.value, ability.children[0]?.value || 0, ability.value, {faded: true}))}
                </div>
              }
            </React.Fragment>;
          })}
        </React.Fragment>
      :
        <React.Fragment>
          <div className={classes.header}><span /><span>{mode == Mode.Done ? 'Target' : 'Attacker'}</span><span>Share</span><span>Damage</span></div>
          {rows.map((p) => {
            const open = expanded[p.key] == true;
            return <React.Fragment key={p.key}>
              {playerRow(p.key, p.value, rows[0]?.value || 0, selectedTotal, {onClick: () => toggle(p.key), chevron: true, open})}
              {open &&
                <div className={classes.nested}>
                  {p.children.map((ability) => abilityRow(`${p.key}|${ability.key}`, ability.key, ability.count, ability.value, p.children[0]?.value || 0, p.value,
                    {faded: true, attacker: mode == Mode.Done ? selected : p.key}))}
                </div>
              }
            </React.Fragment>;
          })}
        </React.Fragment>
      }
    </div>
  );
}

export default AbilityBreakdown;
